<template>
<div class="container-fluid py-4">
<!-- Header Section -->
<div class="row mb-4">
<div class="col-12 d-flex justify-content-between align-items-center flex-wrap gap-3">
<h4 class="text-black fw-bold mb-0">Content Library</h4>

<!-- FLEXIBLE TAB SYSTEM (Supports 10+ tabs with horizontal scroll) -->
    <div class="tabs-outer-container flex-grow-1 mx-3">
      <div class="custom-tabs-wrapper">
        <div 
          class="tab-pill" 
          :class="{ active: viewMode === 'grid' }"
          @click="viewMode = 'grid'; activeTabId = null"
        >
          <i class="fas fa-th-large me-2"></i> Library
        </div>
        
        <div 
          v-for="(tab, index) in deviceTabs" 
          :key="tab.id" 
          class="tab-pill" 
          :class="{ active: viewMode === 'table' && activeTabId === tab.id }"
          @click="openTab(tab.id)"
        >
          <i class="fas fa-desktop me-2" :style="{ color: activeTabId === tab.id ? '#ffffff' : getThemeColor() }"></i>
          {{ tab.device_name }}
          <span class="close-pill ms-2" @click.stop="closeTab(index)">&times;</span>
        </div>

        <!-- + BUTTON MOVED TO END OF LIST -->
        <div class="tab-add-btn ms-1" @click="showDevicePicker = true" title="Open in Table">
          <i class="fas fa-plus">    Add Device</i>
        </div>
      </div>
    </div>

    <div class="d-flex gap-2 align-items-center">
      <!-- NEW: BULK DELETE BUTTON -->
      <button v-if="selectedIds.length > 0" class="btn btn-danger shadow-sm fw-bold text-uppercase" @click="deleteSelected">
        <i class="fas fa-trash-alt me-2"></i> Delete Selected ({{ selectedIds.length }})
      </button>

      <!-- NEW: LIVE VIDEOS BUTTON -->
      <button class="btn btn-outline-danger shadow-sm fw-bold text-uppercase" @click="viewMode = 'live'">
        <i class="fas fa-broadcast-tower me-2"></i> Live Videos
      </button>

      <!-- NEW PREVIEW BUTTON -->
      <button class="btn btn-outline-violet shadow-sm fw-bold text-uppercase" @click="togglePreviewMode">
        <i class="fas fa-play-circle me-2"></i> Preview
      </button>

      <button class="btn btn-lime px-4 shadow-sm text-uppercase" @click="showModal = true">
        <i class="fas fa-upload me-2"></i> Add Content
      </button>
    </div>
  </div>
</div>

<!-- Original Filter View Logic (Visible only in Grid Mode) -->
<div v-if="viewMode === 'grid' || viewMode === 'preview'" class="row mb-4">
  <div class="col-12 d-flex justify-content-between align-items-center">
    <div class="btn-group shadow-sm" role="group">
      <button 
        type="button" 
        class="btn" 
        :class="filterMode === 'active' ? 'btn-lime' : 'btn-outline-secondary'"
        @click="filterMode = 'active'"
      >
        Active Content
      </button>
      <button 
        type="button" 
        class="btn" 
        :class="filterMode === 'expired' ? 'btn-danger-custom' : 'btn-outline-secondary'"
        @click="filterMode = 'expired'"
      >
        Expired Content
      </button>
    </div>

    <!-- NEW: SELECT ALL TOGGLE -->
    <div v-if="viewMode === 'grid' && filteredContentList.length > 0" class="form-check me-2">
      <input class="form-check-input custom-checkbox-main" type="checkbox" id="selectAll" @change="toggleSelectAll($event)">
      <label class="form-check-label fw-bold text-muted ps-1" for="selectAll" style="cursor:pointer">Select All</label>
    </div>
  </div>
</div>

<!-- NEW: LIVE NEWS VIEW MODE (Aaj Tak Live) -->
<div v-if="viewMode === 'live'" class="row">
  <div class="col-12 mb-4">
    <div class="card content-card border-0 shadow-sm overflow-hidden" style="height: 75vh;">
      <div class="card-header bg-white d-flex justify-content-between align-items-center border-0 py-3">
         <h5 class="fw-bold mb-0 text-dark"><i class="fas fa-circle text-danger me-2 animate-pulse"></i> Aaj Tak Live News</h5>
         <button class="btn btn-sm btn-outline-secondary" @click="viewMode = 'grid'"><i class="fas fa-times me-1"></i> Close</button>
      </div>
      <div class="card-body p-0 bg-black">
        <!-- UPDATED: Specific Aaj Tak Live Embed ID to prevent "Unavailable" errors -->
        <iframe 
          width="100%" 
          height="100%" 
          src="https://www.youtube-nocookie.com/embed/Nq2wYlWFucg?autoplay=1&mute=0&rel=0&modestbranding=1&showinfo=0" 
          title="Aaj Tak Live" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
          referrerpolicy="strict-origin-when-cross-origin" 
          allowfullscreen>
        </iframe>
      </div>
    </div>
  </div>
</div>

<!-- PREVIEW MODE (SLIDER VIEW) -->
<div v-if="viewMode === 'preview'" class="row">
  <div v-if="slideshowList.length > 0" class="col-12 mb-4">
    <div class="card content-card border-0 shadow-sm overflow-hidden" :class="{'expired-border': isExpired(slideshowList[activeSlideIndex].expiry_date)}">
      
      <div class="video-box featured-video-container">
        <!-- Smooth Transition for Slideshow -->
        <transition name="slideshow-fade" mode="out-in">
            <div :key="slideshowList[activeSlideIndex].id" class="w-100 h-100">
                <!-- UPDATED: loop removed, @ended and @loadedmetadata added -->
                <!-- UPDATED: src now uses mediaBaseUrl -->
                <video 
                  v-if="slideshowList[activeSlideIndex].file_type === 'video'" 
                  :id="'video-preview-' + slideshowList[activeSlideIndex].id"
                  :src="`${mediaBaseUrl}/${slideshowList[activeSlideIndex].file_path}`" 
                  class="w-100 h-100 featured-media"
                  muted playsinline controls autoplay
                  @play="handlePlay(slideshowList[activeSlideIndex].id)"
                  @pause="handlePause(slideshowList[activeSlideIndex].id)"
                  @ended="jumpToSlide((activeSlideIndex + 1) % slideshowList.length)"
                  @loadedmetadata="onVideoMetadata"
                ></video>
                <!-- UPDATED: src now uses mediaBaseUrl -->
                <img 
                  v-else 
                  :id="'image-preview-' + slideshowList[activeSlideIndex].id"
                  :src="`${mediaBaseUrl}/${slideshowList[activeSlideIndex].file_path}`" 
                  class="w-100 h-100 featured-media object-fit-contain bg-black" 
                />
            </div>
        </transition>
        <span v-if="isExpired(slideshowList[activeSlideIndex].expiry_date)" class="badge bg-danger position-absolute top-0 end-0 m-3">EXPIRED</span>
      </div>

      <div class="card-body p-4 d-flex justify-content-between align-items-center bg-white">
        <div>
            <h5 class="fw-bold mb-1 text-dark">{{ slideshowList[activeSlideIndex].name }}</h5>
            <small class="text-muted">Currently viewing item {{ activeSlideIndex + 1 }} of {{ slideshowList.length }}</small>
        </div>

        <!-- START: UPDATED STATUS BUTTONS -->
        <div class="d-flex gap-3">
          <button class="btn btn-outline-danger px-4 fw-bold text-uppercase shadow-sm" @click="cancelCurrentItem">
            <i class="fas fa-times-circle me-2"></i> Unpublish
          </button>
          <button class="btn btn-lime-solid px-4 fw-bold text-uppercase shadow-sm" @click="publishCurrentItem">
            <i class="fas fa-check-circle me-2"></i> Published
          </button>
        </div>
        <!-- END: NEW BUTTONS -->
        
        <div class="d-flex gap-3">
          <!-- Edit Button for Image in Preview -->
          <button v-if="slideshowList[activeSlideIndex].file_type === 'image'" class="btn-action btn-lg-action btn-orange" @click="editContent(slideshowList[activeSlideIndex])" title="Edit Content">
            <i class="fas fa-edit"></i>
          </button>

          <button v-if="slideshowList[activeSlideIndex].file_type === 'video'" class="btn-action btn-lg-action" :class="playingVideoId === slideshowList[activeSlideIndex].id ? 'btn-red' : 'btn-green'" @click="playVideo('preview-' + slideshowList[activeSlideIndex].id)" title="Play/Pause Video">
            <i :class="playingVideoId === slideshowList[activeSlideIndex].id ? 'fas fa-pause' : 'fas fa-play'"></i>
          </button>

          <button v-if="isExpired(slideshowList[activeSlideIndex].expiry_date)" class="btn-action btn-lg-action btn-green" @click="openRestoreModal(slideshowList[activeSlideIndex])" title="Restore Content">
            <i class="fas fa-undo-alt"></i>
          </button>

          <button class="btn-action btn-lg-action btn-blue" @click="goFullscreen('preview-' + slideshowList[activeSlideIndex].id)">
            <i class="fas fa-expand"></i>
          </button>
          <button class="btn-action btn-lg-action btn-grey" @click="showInfo(slideshowList[activeSlideIndex])">
            <i class="fas fa-info-circle"></i>
          </button>
          <button class="btn-action btn-lg-action btn-red" @click="deleteContent(slideshowList[activeSlideIndex].id)">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- SLIDESHOW INDICATORS BOTTOM SECTION -->
  <div v-if="slideshowList.length > 0" class="col-12 mt-2 mb-5">
    <div class="d-flex flex-column align-items-center">
        <div class="mb-3 text-muted fw-bold small text-uppercase letter-spacing-1">
            NOW PLAYING: <span class="text-magenta">{{ slideshowList[activeSlideIndex]?.name }}</span>
        </div>
        <div class="d-flex justify-content-center gap-3 slideshow-progress-wrapper shadow-sm">
            <div 
                v-for="(item, index) in slideshowList" 
                :key="'slide-dot-' + item.id"
                class="slide-indicator-bar"
                :class="{ 'active': activeSlideIndex === index }"
                @click="jumpToSlide(index)"
            >
                <div 
                  class="inner-progress-fill" 
                  v-if="activeSlideIndex === index"
                  :key="'fill-' + index"
                  :style="{ animationDuration: currentSlideDuration + 's' }"
                ></div>
            </div>
        </div>
    </div>
  </div>

  <div v-if="viewMode === 'preview' && slideshowList.length === 0" class="col-12 text-center py-5">
    <div class="text-muted">
      <i class="fas fa-play-circle fa-3x mb-3 text-light-grey"></i>
      <p>No content is currently published to the slideshow.</p>
      <button class="btn btn-sm btn-lime" @click="viewMode = 'grid'">Go to Library to Publish Content</button>
    </div>
  </div>
</div>

<!-- LIBRARY MODE (GRID VIEW) -->
<div v-if="viewMode === 'grid'" class="row">
  <div v-for="item in filteredContentList" :key="item.id" class="col-xl-3 col-lg-4 col-md-6 mb-4">
    <div class="card h-100 content-card border-0 shadow-sm" :class="{'expired-border': isExpired(item.expiry_date), 'selected-card': selectedIds.includes(item.id)}">
      <div class="video-box">
        
        <!-- NEW: SELECTION OVERLAY -->
        <div class="selection-overlay-container">
          <input type="checkbox" :value="item.id" v-model="selectedIds" class="content-select-checkbox">
        </div>

        <!-- UPDATED: src now uses mediaBaseUrl -->
        <video 
          v-if="item.file_type === 'video'" 
          :id="'video-' + item.id"
          :src="`${mediaBaseUrl}/${item.file_path}`" 
          class="w-100 h-100"
          loop muted playsinline controls
          @play="handlePlay(item.id)"
          @pause="handlePause(item.id)"
        ></video>
        <!-- UPDATED: src now uses mediaBaseUrl and has an ID for fullscreen -->
        <img v-else :id="'image-' + item.id" :src="`${mediaBaseUrl}/${item.file_path}`" class="w-100 h-100 object-fit-cover bg-black" />
        <span v-if="isExpired(item.expiry_date)" class="badge bg-danger position-absolute top-0 end-0 m-2">EXPIRED</span>
        
        <!-- PUBLISHED STATUS BADGE ON IMAGE -->
        <span v-if="item.status === 'published'" class="badge bg-success position-absolute top-0 start-0 m-2 shadow-sm" style="left: 40px !important;">
            <i class="fas fa-check-circle me-1"></i> PUBLISHED
        </span>
        <span v-else class="badge bg-secondary position-absolute top-0 start-0 m-2 shadow-sm" style="left: 40px !important;">
            <i class="fas fa-times-circle me-1"></i> UNPUBLISHED
        </span>
      </div>

      <div class="card-body p-3 d-flex flex-column justify-content-between">
        <div class="d-flex justify-content-between align-items-start mb-2">
            <div class="filename-text fw-bold text-dark pe-2">{{ item.name }}</div>
            <!-- NEW: QUICK TOGGLE SWITCH -->
            <div class="form-check form-switch" title="Toggle Publish Status">
                <input 
                    class="form-check-input custom-switch" 
                    type="checkbox" 
                    :checked="item.status === 'published'"
                    @change="toggleStatus(item, item.status === 'published' ? 'unpublished' : 'published')"
                >
            </div>
        </div>

        <div class="d-flex justify-content-center gap-2 mt-auto pb-2">
          <!-- Edit Button for Image -->
          <button v-if="item.file_type === 'image'" class="btn-orange" @click="editContent(item)" title="Edit Image">
            <i class="fas fa-edit"></i>
          </button>

          <button v-if="item.file_type === 'video'" class="btn-action" :class="playingVideoId === item.id ? 'btn-red' : 'btn-green'" @click="playVideo(item.id)"><i :class="playingVideoId === item.id ? 'fas fa-pause' : 'fas fa-play'"></i></button>
          <button v-if="isExpired(item.expiry_date)" class="btn-action btn-green" @click="openRestoreModal(item)"><i class="fas fa-undo-alt"></i></button>
          <button class="btn-action btn-blue" @click="goFullscreen(item.id)"><i class="fas fa-expand"></i></button>
          <button class="btn-action btn-grey" @click="showInfo(item)"><i class="fas fa-info-circle"></i></button>
          <button class="btn-action btn-red" @click="deleteContent(item.id)"><i class="fas fa-trash-alt"></i></button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="filteredContentList.length === 0" class="col-12 text-center py-5">
    <div class="text-muted">
      <i class="fas fa-folder-open fa-3x mb-3"></i>
      <p>No {{ filterMode }} content found.</p>
    </div>
  </div>
</div>

<!-- UPDATED: TABLE VIEW -->
<div v-if="viewMode === 'table'" class="row">
  <div class="col-12 mb-3">
     <h5 class="fw-bold" :style="{ color: getThemeColor() }">Content for: {{ currentTabFullDetails }}</h5>
  </div>
  
  <div v-for="item in deviceSpecificContent" :key="item.id" class="col-xl-3 col-lg-4 col-md-6 mb-4">
    <div class="card h-100 content-card border-0 shadow-sm" :class="{'expired-border': isExpired(item.expiry_date), 'selected-card': selectedIds.includes(item.id)}">
      
      <div class="video-box">
        
        <!-- NEW: SELECTION OVERLAY FOR TABLE VIEW -->
        <div class="selection-overlay-container">
          <input type="checkbox" :value="item.id" v-model="selectedIds" class="content-select-checkbox">
        </div>

        <!-- UPDATED: src now uses mediaBaseUrl -->
        <video 
          v-if="item.file_type === 'video'" 
          :id="'video-tbl-' + item.id"
          :src="`${mediaBaseUrl}/${item.file_path}`" 
          class="w-100 h-100"
          loop muted playsinline controls
          @play="handlePlay(item.id)"
          @pause="handlePause(item.id)"
        ></video>
        <!-- UPDATED: src now uses mediaBaseUrl and has an ID -->
        <img 
          v-else 
          :id="'image-tbl-' + item.id"
          :src="`${mediaBaseUrl}/${item.file_path}`" 
          class="w-100 h-100 object-fit-cover bg-black" 
        />
        <span v-if="isExpired(item.expiry_date)" class="badge bg-danger position-absolute top-0 end-0 m-2">EXPIRED</span>
        
        <span v-if="item.status === 'published'" class="badge bg-success position-absolute top-0 start-0 m-2 shadow-sm" style="left: 40px !important;">
            <i class="fas fa-check-circle me-1"></i> PUBLISHED
        </span>
        <span v-else class="badge bg-secondary position-absolute top-0 start-0 m-2 shadow-sm" style="left: 40px !important;">
            <i class="fas fa-times-circle me-1"></i> UNPUBLISHED
        </span>
      </div>

      <div class="card-body p-3 d-flex flex-column justify-content-between">
        <div class="d-flex justify-content-between align-items-start mb-2">
            <div class="filename-text fw-bold text-dark pe-2">{{ item.name }}</div>
            <div class="form-check form-switch">
                <input 
                    class="form-check-input custom-switch" 
                    type="checkbox" 
                    :checked="item.status === 'published'"
                    @change="toggleStatus(item, item.status === 'published' ? 'unpublished' : 'published')"
                >
            </div>
        </div>
        
        <div class="d-flex justify-content-center gap-2 mt-auto pb-2">
          <button v-if="item.file_type === 'image'" class="btn-action btn-orange" @click="editContent(item)" title="Edit Image">
            <i class="fas fa-edit"></i>
          </button>

          <button v-if="item.file_type === 'video'" class="btn-action" :class="playingVideoId === item.id ? 'btn-red' : 'btn-green'" @click="playVideo('tbl-' + item.id)" title="Play/Pause Video">
            <i :class="playingVideoId === item.id ? 'fas fa-pause' : 'fas fa-play'"></i>
          </button>

          <button class="btn-action btn-blue" @click="goFullscreen('tbl-' + item.id)">
            <i class="fas fa-expand"></i>
          </button>
          <button class="btn-action btn-grey" @click="showInfo(item)">
            <i class="fas fa-clock"></i>
          </button>
          <button class="btn-action btn-red" @click="deleteContent(item.id)">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="deviceSpecificContent.length === 0" class="col-12 text-center py-5">
    <div class="text-muted">
      <i class="fas fa-ghost fa-3x mb-3"></i>
      <p>No content assigned to this device.</p>
    </div>
  </div>
</div>

<!-- SEARCHABLE DEVICE PICKER MODAL -->
<div v-if="showDevicePicker" class="custom-overlay">
  <div class="picker-modal card shadow-lg border-0">
    <div class="p-4">
      <h5 class="fw-bold mb-3 text-center">Open Table View</h5>
      
      <!-- STEP 1: CITY SELECTION -->
      <div class="mb-3">
        <label class="text-xs fw-bold text-muted ps-1 mb-1">Step 1: Select City</label>
        <select v-model="selectedCityForPicker" class="form-control custom-input" @change="selectedBranchIdForPicker = null">
          <option :value="null">-- Select a City --</option>
          <option v-for="city in uniqueCities" :key="city" :value="city">
            {{ city }}
          </option>
        </select>
      </div>

      <!-- STEP 2: BRANCH SELECTION -->
      <div v-if="selectedCityForPicker" class="mb-3">
        <label class="text-xs fw-bold text-muted ps-1 mb-1">Step 2: Select Branch</label>
        <select v-model="selectedBranchIdForPicker" class="form-control custom-input">
          <option :value="null">-- Select a Branch --</option>
          <option v-for="branch in filteredBranchesForCity" :key="branch.id" :value="branch.id">
            {{ branch.name || branch.branch_name }}
          </option>
        </select>
      </div>

      <!-- STEP 3: DEVICE SELECTION -->
      <div v-if="selectedBranchIdForPicker">
        <label class="text-xs fw-bold text-muted ps-1 mb-1">Step 3: Select Devices</label>
        <div class="search-box mb-2">
            <i class="fas fa-search"></i>
            <input v-model="deviceSearch" type="text" placeholder="Search devices in this branch..." class="form-control custom-input-search">
        </div>

        <div class="device-selection-list custom-scrollbar">
            <div 
            v-for="device in filteredDeviceSearch" 
            :key="device.id" 
            class="device-option"
            :class="{ selected: selectedPickerIds.includes(device.id) }"
            @click="togglePickerDevice(device)"
            >
            <div class="d-flex align-items-center">
                <div class="device-icon-circle me-3">
                    <i class="fas fa-desktop"></i>
                </div>
                <div class="d-flex flex-column">
                    <span class="fw-bold">{{ device.device_name }}</span>
                    <small class="text-muted" style="font-size: 11px;">Position: {{ device.device_position || 'Not Set' }}</small>
                </div>
            </div>
            <i v-if="selectedPickerIds.includes(device.id)" class="fas fa-check-circle text-magenta fs-5"></i>
            <i v-else class="far fa-circle text-light-grey fs-5"></i>
            </div>
            <div v-if="filteredDeviceSearch.length === 0" class="text-center py-4 text-muted">No devices found in this branch.</div>
        </div>
      </div>

      <div v-else-if="!selectedCityForPicker" class="text-center py-5 border rounded bg-light">
          <i class="fas fa-map-marker-alt fa-2x mb-2 text-muted"></i>
          <p class="text-muted small mb-0">Please select a city to view branches.</p>
      </div>

      <div v-else class="text-center py-5 border rounded bg-light">
          <i class="fas fa-building fa-2x mb-2 text-muted"></i>
          <p class="text-muted small mb-0">Please select a branch to view devices.</p>
      </div>

      <div class="d-flex justify-content-center gap-3 mt-4">
        <button class="btn btn-cancel px-4" @click="closePickerModal">CANCEL</button>
        <button class="btn btn-magenta-solid px-4" :disabled="selectedPickerIds.length === 0" @click="confirmDeviceTabs">OPEN SELECTED TABS</button>
      </div>
    </div>
  </div>
</div>

<!-- Modal 1: Upload -->
<div v-if="showModal" class="custom-overlay">
  <div class="custom-modal-card card p-4 shadow-lg border-0" style="width: 500px;">
    <h5 class="modal-title-custom mb-3">Upload New Content</h5>
    
    <div class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">File Name</label>
      <input v-model="form.name" type="text" class="form-control custom-input" placeholder="Enter file name">
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <label class="text-xs fw-bold text-muted ps-1">Assign Branch</label>
        <select v-model="form.branch_id" class="form-control custom-input">
          <option :value="null">All Branches</option>
          <option v-for="branch in branches" :key="branch.id" :value="branch.id">
            {{ branch.name || branch.branch_name }}
          </option>
        </select>
      </div>
      <div class="col-md-6 mb-3">
        <label class="text-xs fw-bold text-muted ps-1">Assign Device</label>
        <select v-model="form.device_id" class="form-control custom-input">
          <option :value="null">All Devices</option>
          <option v-for="device in filteredDevices" :key="device.id" :value="device.id">
            {{ device.device_name }}
          </option>
        </select>
      </div>
    </div>

    <div class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">Specific Location (Optional)</label>
      <input v-model="form.location" type="text" class="form-control custom-input" placeholder="e.g. Second Floor Lobby">
    </div>

    <div class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">File Upload</label>
      <input type="file" @change="onFileSelect" class="form-control custom-input" accept="video/*,image/*">
    </div>

    <!-- NEW: CONDITIONAL DISPLAY TIME FOR IMAGES -->
    <div v-if="isImageSelected" class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">Display Duration (Seconds)</label>
      <input v-model="form.display_time" type="number" class="form-control custom-input" placeholder="e.g. 10">
    </div>

    <div class="mb-4">
        <label class="text-xs fw-bold text-muted ps-1">Expiry Date</label>
        <input v-model="form.expiry_date" type="date" class="form-control custom-input">
    </div>

    <div class="d-flex justify-content-center gap-3">
      <button class="btn btn-cancel px-4" @click="showModal = false">CANCEL</button>
      <button class="btn btn-lime-solid px-4" @click="uploadContent">UPLOAD</button>
    </div>
  </div>
</div>

<!-- NEW: CONDITIONAL DISPLAY TIME FOR IMAGES -->
<div v-if="showEditModal" class="custom-overlay">
  <div class="custom-modal-card card p-4 shadow-lg border-0" style="width: 500px;">
    <h5 class="modal-title-custom mb-3">Edit Content Details</h5>
    
    <div class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">Update Name</label>
      <input v-model="editForm.name" type="text" class="form-control custom-input">
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <label class="text-xs fw-bold text-muted ps-1">Update Branch</label>
        <select v-model="editForm.branch_id" class="form-control custom-input">
          <option :value="null">All Branches</option>
          <option v-for="branch in branches" :key="branch.id" :value="branch.id">
            {{ branch.name || branch.branch_name }}
          </option>
        </select>
      </div>
      <div class="col-md-6 mb-3">
        <label class="text-xs fw-bold text-muted ps-1">Update Device</label>
        <select v-model="editForm.device_id" class="form-control custom-input">
          <option :value="null">All Devices</option>
          <option v-for="device in filteredEditDevices" :key="device.id" :value="device.id">
            {{ device.device_name }}
          </option>
        </select>
      </div>
    </div>

    <div class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">Update Location</label>
      <input v-model="editForm.location" type="text" class="form-control custom-input">
    </div>

    <div v-if="editForm.file_type === 'image'" class="mb-3">
      <label class="text-xs fw-bold text-muted ps-1">Display Duration (Seconds)</label>
      <input v-model="editForm.display_time" type="number" class="form-control custom-input">
    </div>

    <div class="mb-4">
        <label class="text-xs fw-bold text-muted ps-1">Update Expiry Date</label>
        <input v-model="editForm.expiry_date" type="date" class="form-control custom-input">
    </div>

    <div class="d-flex justify-content-center gap-3">
      <button class="btn btn-cancel px-4" @click="showEditModal = false">CANCEL</button>
      <button class="btn btn-lime-solid px-4" @click="updateContent">SAVE CHANGES</button>
    </div>
  </div>
</div>

<!-- MODAL 2: Content Details -->
<div v-if="showInfoModal" class="custom-overlay">
  <div class="info-modal-card card shadow-lg border-0">
    <div class="card-header bg-lime text-white d-flex justify-content-between align-items-center py-3">
      <h6 class="mb-0 text-white fw-bold"><i class="fas fa-info-circle me-2"></i>Content Details</h6>
      <button class="btn-close btn-close-white" @click="showInfoModal = false"></button>
    </div>
    <div class="card-body p-4">
      <div class="d-flex align-items-center mb-3">
        <div class="icon-box-styled me-3"><i class="fas fa-user text-lime"></i></div>
        <div>
          <small class="text-muted d-block text-xs">Uploaded By</small>
          <span class="text-dark fw-bold">{{ selectedInfo.uploader }}</span>
        </div>
      </div>
      <div class="d-flex align-items-center mb-3">
        <div class="icon-box-styled me-3"><i class="fas fa-calendar-alt text-muted"></i></div>
        <div>
          <small class="text-muted d-block text-xs">Upload Date</small>
          <span class="text-dark fw-bold">{{ selectedInfo.date }}</span>
        </div>
      </div>
      <div class="d-flex align-items-center mb-3">
        <div class="icon-box-styled me-3"><i class="fas fa-calendar-times text-danger"></i></div>
        <div>
          <small class="text-muted d-block text-xs">Expiry Date</small>
          <span class="text-dark fw-bold" :class="{'text-danger': filterMode === 'expired'}">{{ selectedInfo.expiry }}</span>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <div class="icon-box-styled me-3"><i class="fas fa-clock text-warning"></i></div>
        <div>
          <small class="text-muted d-block text-xs">Time</small>
          <span class="text-dark fw-bold">{{ selectedInfo.time }}</span>
        </div>
      </div>
    </div>
    <div class="card-footer bg-light border-0 d-flex justify-content-end py-3">
      <button class="btn btn-lime px-4 shadow-sm text-uppercase fw-bold" @click="showInfoModal = false">Got It</button>
    </div>
  </div>
</div>

<!-- MODAL 3: Restore Content -->
<div v-if="showRestoreModal" class="custom-overlay">
  <div class="custom-modal-card card p-4 shadow-lg border-0">
    <h5 class="modal-title-custom mb-3">Restore Content</h5>
    <p class="text-muted small">Select a new expiry date to reactivate this content.</p>
    <div class="mb-4">
        <label class="text-xs fw-bold text-muted ps-1">New Expiry Date</label>
        <input v-model="restoreForm.expiry_date" type="date" class="form-control custom-input">
    </div>
    <div class="d-flex justify-content-center gap-3">
      <button class="btn btn-cancel px-4" @click="showRestoreModal = false">CANCEL</button>
      <button class="btn btn-lime-solid px-4" @click="submitRestore">RESTORE</button>
    </div>
  </div>
</div>
</div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

// REUSABLE TOAST CONFIGURATION
const Toast = Swal.mixin({ // eslint-disable-line no-unused-vars
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
});

export default {
data() {
// GET DYNAMIC API URL FROM ENV
const apiBase = process.env.VUE_APP_API_BASE_URL || '';

// UNIVERSAL LOGIC FOR MEDIA BASE URL
let mediaBase = '';
if (apiBase.startsWith('http')) {
    // Strips everything after the domain (removes /api, /v1, etc.)
    mediaBase = apiBase.split('/v1')[0].split('/api')[0];
    
    // SMART CHECK: Only add '/backend' if we are NOT on localhost
    // This allows localhost to load directly and production to use the Nginx prefix
    if (!mediaBase.includes('localhost')) {
        mediaBase += '/backend';
    }
} else {
    // In production with relative paths (e.g. VUE_APP_API_BASE_URL=/api)
    mediaBase = apiBase.replace('/api', '');
}

return {
apiBaseUrl: apiBase,
mediaBaseUrl: mediaBase, 
contentList: [],
branches: [],
devices: [],
showModal: false,
showInfoModal: false,
showRestoreModal: false,
showEditModal: false,
filterMode: 'active',
playingVideoId: null,
selectedIds: [], // NEW: TRACKS MULTIPLE SELECTED ITEMS
form: {
name: '',
file: null,
expiry_date: '',
branch_id: null,
device_id: null,
location: '',
display_time: 10
},
restoreForm: {
id: null,
expiry_date: ''
},
editForm: {
id: null,
name: '',
expiry_date: '',
branch_id: null,
device_id: null,
location: '',
display_time: 10,
file_type: ''
},
selectedInfo: { uploader: '', date: '', time: '', expiry: '' },

// VIEW & TAB DATA
viewMode: 'grid',
deviceTabs: [],
activeTabId: null,
showDevicePicker: false,
deviceSearch: '',
selectedPickerIds: [],
selectedPickerItems: [],
selectedBranchIdForPicker: null,
selectedCityForPicker: null, // NEW: TRACK CITY SELECTION

// SLIDESHOW DATA
activeSlideIndex: 0,
slideInterval: null,
activeVideoDuration: 0,
skippedItemIds: [] // TRACKS ITEMS HIDDEN ONLY IN CURRENT PREVIEW
};
},
computed: {
filteredContentList() {
const today = new Date();
today.setHours(0, 0, 0, 0);
return this.contentList.filter(item => {
if (!item.expiry_date) return this.filterMode === 'active';
const expiryDate = new Date(item.expiry_date);
const expired = expiryDate < today;
return this.filterMode === 'active' ? !expired : expired;
});
},
// UPDATED COMPUTED PROPERTY: Filters items published in the DB
slideshowList() {
let list = this.filteredContentList;

// 1. Only show published items in slideshow
list = list.filter(item => item.status === 'published');

// 2. Filter by device if a tab is open
if (this.activeTabId) {
list = list.filter(item => item.device_id === this.activeTabId);
}

// 3. Apply temporary skips (if any)
return list.filter(item => !this.skippedItemIds.includes(item.id));
},
filteredDevices() {
if (!this.form.branch_id) return this.devices;
return this.devices.filter(d => d.branch_id === this.form.branch_id);
},
filteredEditDevices() {
if (!this.editForm.branch_id) return this.devices;
return this.devices.filter(d => d.branch_id === this.editForm.branch_id);
},
// NEW COMPUTED: EXTRACT UNIQUE CITIES
uniqueCities() {
const cities = this.branches.map(b => b.city || b.branch_city).filter(Boolean);
return [...new Set(cities)];
},
// NEW COMPUTED: FILTER BRANCHES BY CITY
filteredBranchesForCity() {
if (!this.selectedCityForPicker) return [];
return this.branches.filter(b => (b.city || b.branch_city) === this.selectedCityForPicker);
},
filteredDeviceSearch() {
let list = this.devices;
if (this.selectedBranchIdForPicker) {
list = list.filter(d => d.branch_id === this.selectedBranchIdForPicker);
}
if (!this.deviceSearch) return list;
return list.filter(d => d.device_name.toLowerCase().includes(this.deviceSearch.toLowerCase()));
},
deviceSpecificContent() {
if (!this.activeTabId) return [];
return this.contentList.filter(item => item.device_id === this.activeTabId);
},
// UPDATED: Displays City, Branch, and Device Name
currentTabFullDetails() {
const d = this.deviceTabs.find(t => t.id === this.activeTabId);
if (!d) return '';
return `City: ${d.city} - Branch: ${d.branch} - Device: ${d.device_name}`;
},
isImageSelected() {
return this.form.file && this.form.file.type.startsWith('image/');
},
currentSlideDuration() {
if (!this.slideshowList.length) return 20;
const item = this.slideshowList[this.activeSlideIndex];
if (item.file_type === 'video') return this.activeVideoDuration || 20;
return item.display_time ? parseInt(item.display_time) : 20;
}
},
watch: {
viewMode(newMode) {
if (newMode === 'preview') {
this.skippedItemIds = []; // Reset skipped items when entering preview
this.startSlideshow();
} else {
if (this.slideInterval) clearTimeout(this.slideInterval);
}
}
},
async mounted() {
// Wait for core data to load first so we can find device/branch details
await this.fetchData();
await this.fetchSupportData();

  this.applyDynamicTheme();

  // Listen for theme changes from Navbar
  window.addEventListener('storage', (e) => {
    if (e.key === 'themeColor') {
      this.applyDynamicTheme();
    }
  });

  // Small interval check in case 'storage' event doesn't fire on same tab
  this.themeInterval = setInterval(() => {
    this.applyDynamicTheme();
  }, 1000);

// NEW LOGIC: Check if redirected from Devices page with a specific deviceId
const externalDeviceId = this.$route.query.deviceId;
if (externalDeviceId) {
this.handleExternalDeviceOpen(externalDeviceId);
}
},
beforeUnmount() {
if (this.slideInterval) clearTimeout(this.slideInterval);
if (this.themeInterval) clearInterval(this.themeInterval);
},
methods: {
// NEW: MULTI-SELECT METHODS
toggleSelectAll(event) {
  if (event.target.checked) {
    this.selectedIds = this.filteredContentList.map(item => item.id);
  } else {
    this.selectedIds = [];
  }
},

async deleteSelected() {
  const themeColor = this.getThemeColor();
  Swal.fire({
    title: `Delete ${this.selectedIds.length} items?`,
    text: "Are you sure you want to move selected content to trash?",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Yes, Delete All',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      const token = localStorage.getItem('token');
      try {
        await axios.post(`${this.apiBaseUrl}/v1/admin/content/bulk-delete`, {
          ids: this.selectedIds
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        this.selectedIds = [];
        await this.fetchData();
        Swal.fire({ icon: 'success', title: 'Deleted', text: 'Items moved to trash successfully.', confirmButtonColor: themeColor });
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Bulk delete failed.' });
      }
    }
  });
},

getThemeColor() {
  return localStorage.getItem('themeColor') || '#34d399';
},
applyDynamicTheme() {
  const color = this.getThemeColor();
  const styleId = 'dynamic-content-theme';
  let styleTag = document.getElementById(styleId);
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = styleId;
    document.head.appendChild(styleTag);
  }
  styleTag.innerHTML = `
    /* Main Action Buttons (Add Content, Upload, Save, Restore) */
    .btn-lime, .btn-lime-solid, .btn-magenta-solid, .btn-green, .btn-primary {
      background-color: ${color} !important;
      background-image: none !important;
      border-color: ${color} !important;
      color: white !important;
    }

    /* Active Content Tab */
    .btn-group .btn.btn-lime {
      background-color: ${color} !important;
      color: white !important;
    }

    /* Preview Button (Outline) */
    .btn-outline-violet {
      border-color: ${color} !important;
      color: ${color} !important;
      background: transparent !important;
    }
    .btn-outline-violet:hover {
      background-color: ${color} !important;
      color: white !important;
    }

    /* Top Tabs (Library / Device Tabs) */
    .tab-pill.active {
      background-color: ${color}26 !important; /* 15% opacity background */
      color: white !important;
      box-shadow: inset 0 0 0 1px ${color}4D !important;
    }

    /* Switches / Toggles */
    .custom-switch:checked {
      background-color: ${color} !important;
      border-color: ${color} !important;
    }

    /* Progress bars in preview */
    .inner-progress-fill {
      background-color: ${color} !important;
    }
    
    /* Device Table Headers */
    .row h5 {
      color: ${color} !important;
    }

    /* SELECTION STYLES */
    .selected-card {
      border: 2px solid ${color} !important;
      box-shadow: 0 0 10px ${color}33 !important;
    }

    .custom-checkbox-main:checked {
      background-color: ${color} !important;
      border-color: ${color} !important;
    }
  `;
},
// NEW: Logic to automatically open a device tab when redirected from Devices page
handleExternalDeviceOpen(id) {
// Find the device in the loaded list
const device = this.devices.find(d => String(d.id) === String(id));
if (device) {
// Find branch and city info for the header details
const branch = this.branches.find(b => b.id === device.branch_id);
const branchName = branch ? (branch.name || branch.branch_name) : 'Unknown Branch';
const cityName = branch ? (branch.city || branch.branch_city) : 'Unknown City';

// Add the tab if it's not already in the list
if (!this.deviceTabs.find(t => t.id === device.id)) {
this.deviceTabs.push({
id: device.id,
device_name: device.device_name,
city: cityName,
branch: branchName
});
}

// Set as active and switch to table view
this.activeTabId = device.id;
this.viewMode = 'table';
}
},
async fetchData() {
const token = localStorage.getItem('token');
try {
const res = await axios.get(`${this.apiBaseUrl}/v1/admin/content`, {
headers: { Authorization: `Bearer ${token}` }
});
this.contentList = res.data;
} catch (error) { console.error("Error fetching content:", error); }
},
async fetchSupportData() {
const token = localStorage.getItem('token');
try {
const [bRes, dRes] = await Promise.all([
axios.get(`${this.apiBaseUrl}/v1/admin/branches`, { headers: { Authorization: `Bearer ${token}` }}),
axios.get(`${this.apiBaseUrl}/v1/admin/user-device-list`, { headers: { Authorization: `Bearer ${token}` }})
]);
this.branches = bRes.data;
this.devices = dRes.data;
} catch (error) { console.error("Error fetching branches/devices:", error); }
},

// NEW: TOGGLE STATUS (PUBLISH/UNPUBLISH)
async toggleStatus(item, newStatus) {
const token = localStorage.getItem('token');
try {
await axios.post(`${this.apiBaseUrl}/v1/admin/content/status`, {
id: item.id,
status: newStatus
}, { headers: { Authorization: `Bearer ${token}` }});

// Refresh local data
await this.fetchData();

// Logic to handle active slide if item disappears in preview
if (this.viewMode === 'preview' && this.activeSlideIndex >= this.slideshowList.length) {
this.activeSlideIndex = 0;
}
} catch (error) {
console.error("Status update error:", error);
Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update status." });
}
},

togglePreviewMode() {
this.viewMode = this.viewMode === 'preview' ? 'grid' : 'preview';
this.activeSlideIndex = 0;
},

togglePickerDevice(device) {
const idx = this.selectedPickerIds.indexOf(device.id);
if (idx > -1) {
this.selectedPickerIds.splice(idx, 1);
this.selectedPickerItems.splice(idx, 1);
} else {
this.selectedPickerIds.push(device.id);
this.selectedPickerItems.push(device);
}
},
confirmDeviceTabs() {
const branch = this.branches.find(b => b.id === this.selectedBranchIdForPicker);
const branchName = branch ? (branch.name || branch.branch_name) : 'Unknown Branch';
const cityName = this.selectedCityForPicker || 'Unknown City';

this.selectedPickerItems.forEach(item => {
if (!this.deviceTabs.find(t => t.id === item.id)) {
this.deviceTabs.push({
id: item.id,
device_name: item.device_name,
city: cityName,
branch: branchName
});
}
});
if (this.deviceTabs.length > 0) {
this.activeTabId = this.deviceTabs[this.deviceTabs.length - 1].id;
this.viewMode = 'table';
}
this.closePickerModal();
},
closePickerModal() {
this.showDevicePicker = false;
this.selectedPickerIds = [];
this.selectedPickerItems = [];
this.deviceSearch = '';
this.selectedBranchIdForPicker = null;
this.selectedCityForPicker = null; // RESET CITY ON CLOSE
},
openTab(id) {
this.activeTabId = id;
this.viewMode = 'table';
},
closeTab(index) {
const idToClose = this.deviceTabs[index].id;
this.deviceTabs.splice(index, 1);
if (this.activeTabId === idToClose) {
if (this.deviceTabs.length > 0) {
this.activeTabId = this.deviceTabs[0].id;
} else {
this.viewMode = 'grid';
this.activeTabId = null;
}
}
},

isExpired(date) {
if (!date) return false;
const today = new Date();
today.setHours(0, 0, 0, 0);
return new Date(date) < today;
},
onFileSelect(e) { 
    const file = e.target.files[0];
    if (file && file.size > 50 * 1024 * 1024) {
      Swal.fire({ icon: 'error', title: 'File Too Large', text: 'Maximum file size allowed is 50MB' });
      e.target.value = ''; // Reset input
      return;
    }
    this.form.file = file; 
},
async uploadContent() {
if (!this.form.name || !this.form.file) { Swal.fire({ icon: 'info', text: "Please provide a name and select a file." }); return; }
const formData = new FormData();
formData.append('name', this.form.name);
formData.append('file', this.form.file);
if (this.isImageSelected) formData.append('display_time', this.form.display_time);
if (this.form.expiry_date) formData.append('expiry_date', this.form.expiry_date);
if (this.form.branch_id) formData.append('branch_id', this.form.branch_id);
if (this.form.device_id) formData.append('device_id', this.form.device_id);
if (this.form.location) formData.append('location', this.form.location);
const token = localStorage.getItem('token');
try {
await axios.post(`${this.apiBaseUrl}/v1/admin/content/add`, formData, { headers: { Authorization: `Bearer ${token}` } });
this.form = { name: '', file: null, expiry_date: '', branch_id: null, device_id: null, location: '', display_time: 10 };
this.showModal = false;
this.fetchData();
Swal.fire({ icon: 'success', title: 'Success', text: 'Content uploaded successfully!' });
} catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: "Upload failed" }); }
},

editContent(item) {
this.editForm = {
id: item.id,
name: item.name,
expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : '',
branch_id: item.branch_id,
device_id: item.device_id,
location: item.location || '',
display_time: item.display_time || 10,
file_type: item.file_type
};
this.showEditModal = true;
},
async updateContent() {
if (!this.editForm.name) return Swal.fire({ icon: 'info', text: "Name is required" });
const token = localStorage.getItem('token');
try {
await axios.put(`${this.apiBaseUrl}/v1/admin/content/update/${this.editForm.id}`, {
name: this.editForm.name,
expiry_date: this.editForm.expiry_date,
branch_id: this.editForm.branch_id,
device_id: this.editForm.device_id,
location: this.editForm.location,
display_time: this.editForm.display_time
}, { headers: { Authorization: `Bearer ${token}` }});
this.showEditModal = false;
this.fetchData();
Swal.fire({ icon: 'success', title: 'Success', text: 'Content updated successfully!' });
} catch (error) { console.error("Error updating content:", error); }
},

async deleteContent(id) {
const themeColor = this.getThemeColor();
Swal.fire({
  title: 'Delete item?',
  text: "Are you sure you want to delete this content?",
  icon: 'warning',
  showCancelButton: true,
  confirmButtonColor: '#d33',
  confirmButtonText: 'Yes, Delete',
  cancelButtonText: 'Cancel'
}).then(async (result) => {
  if (result.isConfirmed) {
    const token = localStorage.getItem('token');
    await axios.put(`${this.apiBaseUrl}/v1/admin/content/delete/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    this.fetchData();
    Swal.fire({ icon: 'success', title: 'Success', text: 'Item deleted', confirmButtonColor: themeColor });
  }
});
},
showInfo(item) {
const dt = new Date(item.createdAt);
this.selectedInfo = {
uploader: item.user ? item.user.name : "Admin",
date: dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
time: dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
expiry: item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "None"
};
this.showInfoModal = true;
},
goFullscreen(id) {
  const elem = document.getElementById('video-' + id) || document.getElementById('image-' + id);
  if (elem && elem.requestFullscreen) elem.requestFullscreen();
},
handlePlay(id) { this.playingVideoId = id; },
handlePause(id) { if (this.playingVideoId === id) this.playingVideoId = null; },
playVideo(id) {
const video = document.getElementById('video-' + id);
if (!video) return;
if (video.paused) {
document.querySelectorAll('video').forEach(v => { if (v.id !== 'video-' + id) v.pause(); });
video.play();
} else { video.pause(); }
},
openRestoreModal(item) {
this.restoreForm.id = item.id;
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
this.restoreForm.expiry_date = tomorrow.toISOString().split('T')[0];
this.showRestoreModal = true;
},
async submitRestore() {
if (!this.restoreForm.expiry_date) return Swal.fire({ icon: 'info', text: "Select a date." });
const token = localStorage.getItem('token');
try {
await axios.put(`${this.apiBaseUrl}/v1/admin/content/update/${this.restoreForm.id}`, { expiry_date: this.restoreForm.expiry_date }, { headers: { Authorization: `Bearer ${token}` }});
this.showRestoreModal = false;
this.fetchData();
Swal.fire({ icon: 'success', title: 'Success', text: 'Content restored successfully!' });
} catch (error) { console.error("Error restoring:", error); }
},

// SLIDESHOW METHODS
onVideoMetadata(event) {
this.activeVideoDuration = event.target.duration;
},
startSlideshow() {
if (this.slideInterval) clearTimeout(this.slideInterval);

if (this.slideshowList.length > 1 && this.viewMode === 'preview') {
const currentItem = this.slideshowList[this.activeSlideIndex];
if (currentItem.file_type === 'video') return;

const durationMs = this.currentSlideDuration * 1000;
this.slideInterval = setTimeout(() => {
this.activeSlideIndex = (this.activeSlideIndex + 1) % this.slideshowList.length;
this.startSlideshow();
}, durationMs);
}
},
jumpToSlide(index) {
this.activeSlideIndex = index;
this.activeVideoDuration = 0;
this.startSlideshow();
},

// UPDATED PREVIEW BUTTONS: Now using DB status
cancelCurrentItem() {
const itemToUnpublish = this.slideshowList[this.activeSlideIndex];
if (itemToUnpublish) {
this.toggleStatus(itemToUnpublish, 'unpublished');
}
},
publishCurrentItem() {
// It is already published if it's here, so just move to next
this.jumpToSlide((this.activeSlideIndex + 1) % this.slideshowList.length);
}
}
};
</script>

<style scoped>
/* ==========================
   COLOR SCHEME OVERRIDES
========================== */
.text-mint { color: #34d399 !important; }
.text-violet { color: #a78bfa !important; }

/* ==========================
   BUTTONS (Mint & Violet)
========================== */
.btn-lime { border-radius: 10px; font-weight: bold; border: none; }
.btn-lime-solid { font-weight: bold; border-radius: 10px; border: none; }
.btn-outline-violet { font-weight: bold; border-radius: 10px; transition: all 0.2s; }
.btn-outline-primary { border: 2px solid #a78bfa !important; color: #a78bfa !important; }
.btn-outline-primary:hover { background: #a78bfa !important; color: white !important; }

/* ==========================
   TABS SYSTEM (Dark Theme)
========================== */
.custom-tabs-wrapper {
  background: #0d2b2b; /* Sidebar Background */
  border-radius: 12px;
  padding: 6px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
}
.tab-pill {
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: white !important; /* Pure white */
  display: flex;
  align-items: center;
  transition: 0.25s;
  min-width: 130px;
}
.tab-add-btn {
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  
}
.tab-add-btn:hover { background: rgba(167, 139, 250, 0.1); }

/* ==========================
   SEARCH & INPUTS (Removed Pink)
========================== */
.custom-input:focus, .custom-input-search:focus {
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.15) !important;
}

/* Existing Animations & Layout Logic Preserved */
@keyframes pulse-red {
0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
}
.animate-pulse { animation: pulse-red 2s infinite; border-radius: 50%; }
.slideshow-fade-enter-active, .slideshow-fade-leave-active { transition: opacity 0.8s ease, transform 0.8s ease; }
.slideshow-fade-enter-from { opacity: 0; transform: scale(0.98); }
.slideshow-fade-leave-to { opacity: 0; transform: scale(1.02); }
.featured-video-container { height: 500px !important; border-radius: 20px 20px 0 0; position: relative; background: #000; overflow: hidden; }
.slideshow-progress-wrapper { background: #ffffff; padding: 15px 35px; border-radius: 50px; border: 1px solid #e2e8f0; }
.slide-indicator-bar { width: 50px; height: 12px; background: #edf2f7; border-radius: 6px; cursor: pointer; position: relative; overflow: hidden; transition: width 0.5s; }
.slide-indicator-bar.active { width: 110px; background: rgba(52, 211, 153, 0.1); }
.tabs-outer-container { overflow-x: auto; white-space: nowrap; scrollbar-width: thin; padding-bottom: 5px; }
.content-card { background: #ffffff; border-radius: 15px !important; position: relative; overflow: hidden; }
.video-box { background: #000; height: 180px; width: 100%; position: relative; }
.filename-text { color: #333; font-size: 14px; min-height: 40px; font-weight: 600; }
.btn-action { width: 40px; height: 36px; border: none; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; transition: 0.2s; }
.btn-blue { background-color: #007bff; }
.btn-grey { background-color: #6c757d; }
.btn-red { background-color: #dc3545; }
.btn-orange { background-color: #ff9800; }
.custom-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.6); z-index: 1050; display: flex; justify-content: center; align-items: center; }
.info-modal-card { width: 380px; border-radius: 20px; overflow: hidden; }
.icon-box-styled { width: 48px; height: 48px; background: #f0f2f5; border-radius: 12px; display: flex; align-items: center; justify-content: center; }

/* NEW SELECTION STYLES */
.selection-overlay-container {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 100;
}
.content-select-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #34d399; /* Matches default theme */
}
</style>